"use client"

import { useEffect, useId, useState } from "react"
import { Label, Textarea } from "@ui/shad"

import { SimpleFormDialog } from "@/components/dialogs"
import { PartnerFeatureDialogGate } from "@/components/partners/partner-feature-gate"

interface ReprocessRecordingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (additionalInstructions?: string) => Promise<void>
  isPending: boolean
}

function ReprocessRecordingDialogContent({ open, onOpenChange, onConfirm, isPending }: ReprocessRecordingDialogProps) {
  const dialogFormId = useId()

  const [instructions, setInstructions] = useState("")

  useEffect(() => {
    if (!open) setInstructions("")
  }, [open])

  const handleConfirm = async () => {
    try {
      await onConfirm(instructions.trim() || undefined)
    } catch {
      return
    }
  }

  return (
    <SimpleFormDialog
      dismiss="explicit"
      title={"Reprocess recording with AI?"}
      open={open}
      onOpenChange={onOpenChange}
      action={{ label: "Reprocess recording", form: dialogFormId, disabled: isPending, pending: isPending }}
      cancel={{ label: "Cancel", onClick: () => onOpenChange(false), disabled: isPending }}
    >
      <form
        id={dialogFormId}
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (isPending) return
          void handleConfirm()
        }}
      >
        {
          <>
            <div className="space-y-4">
              <p>
                A successful attempt replaces the current transcript and regenerates the meeting notes. You can only do
                this once.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="reprocess-recording-instructions" className="text-foreground text-sm font-medium">
                  Additional instructions (optional)
                </Label>
                <Textarea
                  id="reprocess-recording-instructions"
                  value={instructions}
                  onChange={(event) => setInstructions(event.target.value)}
                  placeholder="e.g. Focus the notes on decisions, follow-ups, and tax planning details"
                  className="min-h-24 resize-none"
                  disabled={isPending}
                />
              </div>
            </div>
          </>
        }
      </form>
    </SimpleFormDialog>
  )
}

export default function ReprocessRecordingDialog(props: React.ComponentProps<typeof ReprocessRecordingDialogContent>) {
  return (
    <PartnerFeatureDialogGate
      feature="notetaker"
      title="Reprocess recording"
      open={props.open}
      onOpenChange={props.onOpenChange}
    >
      <ReprocessRecordingDialogContent {...props} />
    </PartnerFeatureDialogGate>
  )
}
